import type { Document } from '@/shared/api/transcriptor';

import {
  isProtocolDocument,
  type DocumentTemplateSummary,
  type EncounterSchema,
  type SchemaDocumentRef,
} from '@/entities/templates';

export type SchemaDocument = {
  reference: SchemaDocumentRef;
  template: DocumentTemplateSummary;
};

export const getSchemaDocuments = (
  schema: EncounterSchema | undefined,
  templates: DocumentTemplateSummary[],
): SchemaDocument[] =>
  (schema?.documents ?? []).flatMap((reference) => {
    const template = templates.find(
      (item) => item.series_id === reference.template_series_id,
    );
    return template ? [{ reference, template }] : [];
  });

// Protocols are chosen when the encounter starts; afterwards only the other
// on-demand documents (letter, literature review) can be added.
export const getAddableDocuments = ({
  schema,
  templates,
  documents,
}: {
  schema: EncounterSchema | undefined;
  templates: DocumentTemplateSummary[];
  documents: Document[];
}): SchemaDocumentRef[] =>
  (schema?.documents ?? []).filter(
    (reference) =>
      reference.inclusion === 'on_demand' &&
      !!reference.template_series_id &&
      !isProtocolDocument(reference, templates) &&
      !documents.some(
        (document) =>
          document.template_series_id === reference.template_series_id,
      ),
  );

export const getSchemaDocumentLabel = (
  reference: SchemaDocumentRef,
  templates: DocumentTemplateSummary[],
) => {
  const template = templates.find(
    (item) => item.series_id === reference.template_series_id,
  );
  return (
    reference.tab_title ||
    template?.tab_title ||
    reference.name ||
    template?.name ||
    reference.code
  );
};
